import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Star, ExternalLink, Instagram, Globe, Calendar, Music, Play } from 'lucide-react';
import api from '../services/api';

const HighlightedArtists = ({ limit = 6, showTitle = true, showViewAll = true, expanded = false, className = "" }) => {
    const [highlightedArtists, setHighlightedArtists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchHighlightedArtists();
    }, [limit]);

    const fetchHighlightedArtists = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await api.get(`/admin/highlighted-artists/public?limit=${limit}`);
            
            if (response.data.success) {
                setHighlightedArtists(response.data.data);
            } else {
                setError('Failed to load highlighted artists');
            }
        } catch (err) {
            console.error('Error fetching highlighted artists:', err);
            setError('Unable to load highlighted artists');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className={`glass-card p-6 ${className}`}>
                {showTitle && (
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Star className="w-5 h-5 text-yellow-400" />
                            Highlighted Artists
                        </h3>
                    </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(3)].map((_, index) => (
                        <div key={index} className="glass-card p-4 animate-pulse">
                            <div className="w-32 h-32 bg-gray-700 rounded-full mx-auto mb-3"></div>
                            <div className="h-4 bg-gray-700 rounded mb-2"></div>
                            <div className="h-3 bg-gray-700 rounded w-3/4 mx-auto"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={`glass-card p-6 ${className}`}>
                {showTitle && (
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Star className="w-5 h-5 text-yellow-400" />
                            Highlighted Artists
                        </h3>
                    </div>
                )}
                <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">{error}</p>
                </div>
            </div>
        );
    }

    if (!highlightedArtists || highlightedArtists.length === 0) {
        return (
            <div className={`glass-card p-6 ${className}`}>
                {showTitle && (
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                            <Star className="w-5 h-5 text-yellow-400" />
                            Highlighted Artists
                        </h3>
                    </div>
                )}
                <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-400">No highlighted artists at the moment</p>
                    <p className="text-gray-500 text-base mt-2">Check back soon to discover amazing artists!</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`glass-card p-6 ${className}`}>
            {showTitle && (
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-400" />
                        Highlighted Artists
                    </h3>
                    {showViewAll && (
                        <Link
                            to="/artists"
                            className="text-yellow-400 hover:text-yellow-300 text-base flex items-center gap-1"
                        >
                            View All <ExternalLink className="w-3 h-3" />
                        </Link>
                    )}
                </div>
            )}

            <div className={`grid gap-6 ${expanded ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                {highlightedArtists.map((artist) => (
                    <div
                        key={artist.id}
                        className="glass-card p-5 hover:transform hover:scale-105 transition-all duration-300 group"
                    >
                        <div className="text-center">
                            {/* Profile Picture */}
                            <div className="relative mb-4">
                                {artist.profile_picture_url ? (
                                    <img
                                        src={artist.profile_picture_url}
                                        alt={artist.display_name || artist.username}
                                        width="120"
                                        height="120"
                                        className="rounded-full mx-auto object-cover border-2 border-yellow-400/50 group-hover:border-yellow-400 transition-colors"
                                        style={{ maxWidth: '75%', margin: '0 auto' }}
                                        onLoad={(e) => {
                                            e.target.style.setProperty('max-width', '75%', 'important');
                                            e.target.style.setProperty('margin', '0 auto', 'important');
                                        }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : null}
                                <div
                                    className={`w-32 h-32 rounded-full mx-auto flex items-center justify-center bg-gradient-to-r from-yellow-500 to-orange-500 border-2 border-yellow-400/50 group-hover:border-yellow-400 transition-colors ${
                                        artist.profile_picture_url ? 'hidden' : 'flex'
                                    }`}
                                    style={{ maxWidth: '75%' }}
                                >
                                    <span className="text-4xl leading-none">🌊</span>
                                </div>
                                {/* Star highlight badge */}
                                <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                                    <Star className="w-3 h-3 text-yellow-900 fill-current" />
                                </div>
                                {/* Artist verification badge */}
                                {Boolean(artist.artist_verified) && (
                                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                                        <Music className="w-3 h-3 text-white" />
                                    </div>
                                )}
                            </div>

                            {/* Artist Name */}
                            <Link to={`/profile/${artist.username}`} className="block text-white font-bold text-lg mb-1 hover:text-yellow-300 transition-colors">
                                {artist.display_name || artist.username}
                            </Link>
                            
                            {/* Artist Role/Type */}
                            <p className="text-yellow-400 text-sm font-semibold mb-3">
                                {artist.is_artist ? 'Featured Artist' : 'Highlighted Member'}
                            </p>

                            {/* Description */}
                            {artist.highlighted_artist_description && (
                                <p className="text-gray-300 text-sm mb-3 line-clamp-2 leading-relaxed">
                                    {artist.highlighted_artist_description}
                                </p>
                            )}

                            {/* Artist Bio (if no description) */}
                            {!artist.highlighted_artist_description && artist.bio && (
                                <p className="text-gray-400 text-sm mb-3 line-clamp-2 leading-relaxed">
                                    {artist.bio}
                                </p>
                            )}

                            {/* Highlighted Date */}
                            <div className="flex items-center justify-center text-gray-500 text-sm mb-3">
                                <Calendar className="w-3 h-3 mr-1" />
                                <span>
                                    Featured {new Date(artist.highlighted_added_date || artist.created_at).toLocaleDateString('en-US', {
                                        month: 'short',
                                        year: 'numeric'
                                    })}
                                </span>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 justify-center">
                                <Link
                                    to={`/profile/${artist.username}`}
                                    className="flex-1 py-2 px-3 bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 text-sm rounded-lg transition-colors duration-200 border border-yellow-600/30 text-center"
                                >
                                    View Profile
                                </Link>
                                {Boolean(artist.is_artist) && (
                                    <Link
                                        to={`/profile/${artist.username}`}
                                        aria-label="Play"
                                        className="py-2 px-3 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 text-sm rounded-lg transition-colors duration-200 border border-purple-600/30 flex items-center justify-center"
                                    >
                                        <Play className="w-3 h-3" />
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Load More hint if there are more artists */}
            {highlightedArtists.length >= limit && showViewAll && (
                <div className="text-center mt-6">
                    <p className="text-gray-400 text-base mb-3">
                        Showing {highlightedArtists.length} highlighted artists
                    </p>
                    <Link
                        to="/artists"
                        className="text-yellow-400 hover:text-yellow-300 text-base flex items-center gap-1 mx-auto"
                    >
                        Discover More Artists <ExternalLink className="w-3 h-3" />
                    </Link>
                </div>
            )}
        </div>
    );
};

export default HighlightedArtists;